function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React from 'react';
import { string, bool, oneOf, number, func, node } from 'prop-types';
import cn from 'clsx';
import { designTokenPropTypes } from '@immediate_media/design-tokens';
import { icons } from '@immediate_media/icons';
import { dataProps } from '../../utils/index.js';
import Icon from '../../atoms/Icon/index.jsx';
import { useDesignTokens } from '../../hooks/index.js';

/**
 * Button component.
 */
const Button = ({
  as,
  dataComponentVariant,
  dataFeature,
  text,
  fillHeight,
  fluid,
  disabled,
  wide,
  adaptive,
  link,
  href,
  transparent,
  small,
  secondary,
  inverted,
  icon,
  iconSize,
  iconSpinning,
  iconAnimationDuration,
  iconRotate,
  color,
  className,
  noMinWidth,
  inputPrepend,
  inputAppend,
  light,
  onClick,
  ariaLabel,
  type,
  children,
  noPadding,
  noBorder,
  noFocusOutline,
  noTab,
  highlight,
  gtmLabel,
  role,
  ariaChecked,
  ariaLabelledBy,
  id,
  target,
  ...restProps
}) => {
  const TagName = as || 'button';
  const {
    colors
  } = useDesignTokens();
  return /*#__PURE__*/React.createElement(TagName, _extends({}, dataProps(restProps), {
    className: cn('button', className, fillHeight && 'button--fill-height', fluid && 'button--fluid', wide && 'button--wide', adaptive && 'button--adaptive', link && 'button--link', transparent && 'button--transparent', small && 'button--small', secondary && 'button--secondary', inverted && 'button--inverted', icon && 'button--icon', noMinWidth && 'button--no-min-width', inputPrepend && 'button--input-prepend', inputAppend && 'button--input-append', light && 'button--light', noPadding && 'button--no-padding', noBorder && 'button--no-border', highlight && 'button--highlight', noFocusOutline && 'button--no-focus-outline'),
    disabled: disabled,
    onClick: onClick,
    "aria-label": ariaLabel,
    type: TagName === 'button' ? type : null,
    href: href,
    tabIndex: noTab ? -1 : null,
    "data-component": "Button",
    "data-component-variant": dataComponentVariant || gtmLabel,
    "data-feature": dataFeature,
    "data-gtm-class": gtmLabel,
    role: role,
    "aria-checked": ariaChecked,
    "aria-labelledby": ariaLabelledBy,
    id: id,
    target: TagName === 'a' ? target : null
  }), /*#__PURE__*/React.createElement("div", {
    className: "button__content",
    style: {
      color: color ? colors[color] : null
    }
  }, icon && /*#__PURE__*/React.createElement(Icon, {
    name: icon,
    size: iconSize,
    spinning: iconSpinning,
    animationDuration: iconAnimationDuration,
    rotate: iconRotate,
    color: "inherit",
    className: text && 'mr-xs'
  }), children || text));
};
Button.defaultProps = {
  as: null,
  dataComponentVariant: null,
  dataFeature: null,
  fillHeight: false,
  fluid: false,
  text: '',
  disabled: false,
  wide: false,
  adaptive: false,
  transparent: false,
  noMinWidth: false,
  small: false,
  link: false,
  href: null,
  secondary: false,
  inverted: false,
  icon: null,
  iconSize: 20,
  iconSpinning: null,
  iconAnimationDuration: null,
  iconRotate: null,
  color: null,
  className: null,
  inputPrepend: false,
  inputAppend: false,
  onClick: null,
  ariaLabel: null,
  type: 'button',
  children: null,
  light: null,
  noPadding: false,
  noBorder: false,
  noFocusOutline: false,
  noTab: null,
  highlight: false,
  gtmLabel: null,
  role: 'button',
  ariaChecked: undefined,
  ariaLabelledBy: undefined,
  id: undefined,
  target: null
};
Button.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Render with an alternative root tag (e.g. an <a> instead of a <button>). */
  as: string,
  /** Value for a custom data variant attribute */
  dataComponentVariant: string,
  /** Value for a custom data attribute */
  dataFeature: string,
  /** The text for the button. */
  text: string,
  /** Fill the height of the container. */
  fillHeight: bool,
  /** Stretch the button to the width of its container. */
  fluid: bool,
  /** link for alternative root tag. */
  href: string,
  /** Disable the button. */
  disabled: bool,
  /** Render a wide button. */
  wide: bool,
  /** Render an adaptive button. */
  adaptive: bool,
  /** Render with the appearance of a link. */
  link: bool,
  /** Render as text only. */
  transparent: bool,
  /** Render a small button. */
  small: bool,
  /** Render a secondary button. */
  secondary: bool,
  /** Render an inverted button; generally used for dark backgrounds. */
  inverted: bool,
  /** Render a light button */
  light: bool,
  /** Add an icon to the button. */
  icon: oneOf(Object.keys(icons)),
  /** The size of any icon, in pixels. */
  iconSize: number,
  /** Spin the icon. */
  iconSpinning: bool,
  /** The duration of any icon animation in milliseconds. */
  iconAnimationDuration: number,
  /** The degrees by which to rotate the icon. */
  iconRotate: number,
  /** The text & icon colour. */
  color: designTokenPropTypes.color,
  /** Additional class names to add to the component root. */
  className: string,
  /** Remove the min width from the button. */
  noMinWidth: bool,
  /** Style as a button to be prepended to input fields. */
  inputPrepend: bool,
  /** Style as a button to be appended to input fields. */
  inputAppend: bool,
  /** On click event handler. */
  onClick: func,
  /** The aria-label attribute. */
  ariaLabel: string,
  /** The button type. */
  type: oneOf(['button', 'submit', 'reset']),
  /** The component's children. */
  children: node,
  /** Toggle padding on the button content */
  noPadding: bool,
  /** Remove the border. */
  noBorder: bool,
  /** Remove the outline when focused. */
  noFocusOutline: bool,
  /** Make the button not tabbable. */
  noTab: bool,
  /** Render a button using the highlight colour. */
  highlight: bool,
  /** A label to be used for GTM tracking. */
  gtmLabel: string,
  /** The aria role of the button. */
  role: string,
  /** Used with role="switch" to indicate on/off state. */
  ariaChecked: bool,
  /** The id of a component that labels this button for more information. */
  ariaLabelledBy: string,
  /** An id for the button. */
  id: string,
  /** A target for the opening a link in a new tab. */
  target: string
} : {};
export default Button;